import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Box } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import LoginLayout from 'components/system/layouts/LoginLayout';
import ScreenContext from 'Contexts/Common/ScreenContext';
import { useAppDispatch, useAppSelector } from 'redux/hooks';
import OutlineTextBox from 'components/system/atoms/textBoxs/OutlineTextBox';
import DoneButton from 'components/system/atoms/buttons/DoneButton';
import HttpConnection from 'utils/httpConnection';
import { REST_API } from 'constants/apiUrls';
import redirectLinks from 'constants/redirectLinks';
import LoginLogo from 'components/login/layouts/loginLogo';
import prizeMachineImage from 'assets/images/machine.png';
import MESSAGES from 'constants/messages';
import { handleCallFrontError, setScreenId } from 'redux/slices/commonSlice';
import MessageHelper from 'utils/message.helper';
/* ************ Style ************ */
const useStyles = makeStyles()(() => ({
  contens: {
    display: 'flex',
    justifyContent: 'space-between',
    padding: '111px 48px 23px 48px',
    '& .leftArea': {
      width: '436px',
      '& .systemLogo': {
        height: '50px',
        marginTop: 23,
      },
      '& .systemImage': {
        position: 'absolute',
        width: 652,
        top: 198,
        left: -80,
        zIndex: 0,
      },
    },
    '& .rightArea': {
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      width: '60%',
      height: '586px',
      padding: '20px 48px 80px 48px',
      background: '#E5E5E5',
      borderRadius: '8px',
      zIndex: 1,
      '& .title': {
        fontSize: '40px',
      },
      '& .subTitle': {
        marginTop: '24px',
        fontSize: '24px',
      },
      '& .accountId' :{
        marginTop: '24px',
        fontSize: '24px',
        textAlign: 'left',
        width: '560px',
      }
    },
  },
  inputArea: {
    width: '560px',
  },
  textBox: {
    marginTop: '24px',
    '& .outlinedInput': {
      height: '60px',
    },
  },
  doneBtn: {
    height: '60px',
    marginTop: '68px',
    fontSize: '28px',
    width: '560px',
  },
}));
/* ************ Context ************ */
const ScreenContextValue = {
  title: 'メールアドレス設定',
  screenId: 'SCR014',
  currentMenu: '',
};
/* ************ Component ************ */
const Login: React.FC<React.PropsWithChildren<unknown>> = () => {
  const { classes } = useStyles();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  /* ************ state/redux ************ */
  // 画面項目用
  const [inputValue, setInputValue] = useState<string>("");
  const [confirmInputValue, setConfirmInputValue] = useState<string>("");
  const [versionNo, setVersionNo] = useState<number>(0);
  const userInfo = useAppSelector((state) => state.common.userInfo);
  /* ************ Event ************ */
  // 画面項目のチェンジイベント
  const handleChangeInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setInputValue(ev.target.value);
  };
  const handleChangeConfirmInput = (ev: React.ChangeEvent<HTMLInputElement>): void => {
    setConfirmInputValue(ev.target.value);
  }

  /* ************ Effect ************ */
  useEffect(() => {
    getVersionApi();
    dispatch(setScreenId('SCR014'));
  }, []);

  /* ************ API ************ */
  // メールアドレス登録API
  const handleClickDone = async () => {
    // バリデーションチェック関数
    if(!validate()) return;
    const userId = userInfo?.userId;
    const http = new HttpConnection({ dispatch });
        await http
          .post<boolean>(REST_API.LOGIN.SAVE_EMAIL, {userId: userId, email: inputValue, versionNo: versionNo})
          .then((data) => {
              if (data) {
                dispatch(setScreenId(''));
                navigate(redirectLinks.ONE_TIME_LOGIN);
              }
          });
  };

  // userテーブルのversionNoの取得
  const getVersionApi = async () => {
    const http = new HttpConnection({ dispatch });
    const data = await http.get<number>(REST_API.LOGIN.GET_USER_VERSION_NO, {userId: userInfo?.userId});
    if (data) {
      setVersionNo(data);
    }
  };

  /* ************ Event ************ */
  // バリデーションチェック関数
  const validate = (): boolean => {
    // メールアドレス形式かどうかのチェック時に使用する正規表現
    const emailPattern = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    if (inputValue === '') {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MESSAGES.ERR610)] },}),);
      return false; // バリデーションエラーの場合は false を返す
    }
    if (!emailPattern.test(inputValue)) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MESSAGES.ERR611)] },}),);
      return false;
    }
    if (inputValue !== confirmInputValue) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MESSAGES.ERR609)] },}),);
      return false;
    }
    // 255文字以上の場合はエラー
    if (inputValue.length > 255) {
      dispatch(handleCallFrontError({errors: { key: [MessageHelper.messageFormat(MESSAGES.INF111, '255')] },}),);
      return false;
    }

  return true; // バリデーション成功の場合は true を返す
};


  return (
    <ScreenContext.Provider value={ScreenContextValue}>
      <LoginLayout>
        <Box className={classes.contens}>
          <Box className="leftArea">
            <Box className="systemLogo">
              <LoginLogo />
            </Box>
            <Box className="systemImage">
              <img src={prizeMachineImage} alt="プライズ機イメージ" />
            </Box>
          </Box>
          <Box className="rightArea">
            <div className="title">メールアドレス設定</div>
            <div className='subTitle'>ワンタイムパスワードの受信先となるメールアドレスを設定してください。</div>
            <div className='accountId'>アカウントID：{userInfo?.accountId}</div>
            <Box className={classes.inputArea}>
              <OutlineTextBox
                id="email"
                name="email"
                value={inputValue}
                placeholder="メールアドレス"
                type='text'
                onChange={handleChangeInput}
                className={classes.textBox}
                fullWidth
              />
              <OutlineTextBox
                id="confirmEmail"
                name="confirmEmail"
                value={confirmInputValue}
                placeholder="メールアドレス（確認）"
                type='text'
                onChange={handleChangeConfirmInput}
                className={classes.textBox}
                fullWidth
              />
            </Box>
            <DoneButton className={classes.doneBtn} onClick={handleClickDone} fullWidth>
              設定
            </DoneButton>
          </Box>
        </Box>
      </LoginLayout>
    </ScreenContext.Provider>
  );
};
export default Login;